import React, { ReactElement, useEffect, useState } from 'react'
import 'react-image-crop/dist/ReactCrop.css'
import ReactCrop, { Crop, ReactCropState } from 'react-image-crop'
import styled, { keyframes } from 'styled-components'
import { CgArrowsExpandDownRight, CgArrowsExpandDownLeft } from 'react-icons/cg'
import { getCroppedImage } from './imageHelper'
import { ImageFile, IPixelAspect } from './imageTypes'
import colors from '../lib/colors'
import Modal from '../components/Modal'
import InputGroup from '../components/InputGroup'
import Button, { ButtonStyle } from '../buttons/Button'
import { StateAction } from '../types/typeAliases'

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`
// Hides the glitchy first render of react-image-crop
const StyledModal = styled(Modal)`
  animation: ${fadeIn} 0.5s;
`
const Styles = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 10px;
  user-select: none;
`
const UserGuide = styled.div`
  .crop-help {
    color: ${colors.white};
    background: ${colors.shadow};
    pointer-events: none;
    position: relative;
    transition: opacity 1s cubic-bezier(0.75, 0, 1, 0.5);
  }
  svg {
    opacity: 0.5;
    width: 50px;
    height: auto;
    position: absolute;
  }
  .crop-left-arrow {
    top: -15px;
    left: -15px;
  }
  .crop-right-arrow {
    top: -15px;
    right: -15px;
  }
  div {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
  }
  .crop-help-fade-out {
    opacity: 0;
  }
`
export interface ICropImageProps {
  imageToCrop: ImageFile
  setCropImage: StateAction<false | ImageFile>
  saveCrop: (file: File) => void
  cropPixelAspect?: IPixelAspect
  cropPixelMinimumAspect?: IPixelAspect
  isPng?: true
}

export const getCrop = (
  imageDimensions?: IPixelAspect,
  cropAspect?: IPixelAspect,
): Crop => {
  if (!imageDimensions || !cropAspect) {
    // if no aspect ratio, fill the image
    return {
      unit: '%',
      width: 100,
      height: 100,
      x: 0,
      y: 0,
    }
  }
  // else fill the image in the shortest relative direction.

  // get the width when the crop is set to same height as the image...
  const expandedCropWidth = cropAspect.x * (imageDimensions.y / cropAspect.y)
  // ... and get height when same width as the image
  const expandedCropHeight = cropAspect.y * (imageDimensions.x / cropAspect.x)

  const expandedCropWidthArea = expandedCropWidth * imageDimensions.y
  const expandedCropHeightArea = expandedCropHeight * imageDimensions.x

  // whichever has the smaller area is the one that fits within the image
  if (expandedCropWidthArea < expandedCropHeightArea) {
    return {
      unit: '%',
      // set the height of the crop box to fill the supplied image
      height: 100,
      // set the width as per our chosen aspect ratio
      width: (expandedCropWidth / imageDimensions.x) * 100,
      // put our crop box in the middle of the image
      x:
        ((imageDimensions.x - expandedCropWidth) / 2 / imageDimensions.x) * 100,
      y: 0,
    }
  }

  return {
    unit: '%',
    // set the width of the crop box to fill the supplied image
    width: 100,
    // set the height as per our chosen aspect ratio
    height: (expandedCropHeight / imageDimensions.y) * 100,
    // put our crop box in the middle of the image
    x: 0,
    y: ((imageDimensions.y - expandedCropHeight) / 2 / imageDimensions.y) * 100,
  }
}

function ImageCrop(props: ICropImageProps): ReactElement {
  const {
    imageToCrop,
    setCropImage,
    cropPixelAspect,
    saveCrop,
    cropPixelMinimumAspect,
    isPng,
  } = props

  const initCrop = getCrop(imageToCrop.dimensions, cropPixelAspect)

  const [imageUrl, setImageUrl] = useState<string>('')
  useEffect(() => {
    if (!imageUrl) setImageUrl(URL.createObjectURL(imageToCrop.file))
  }, [])

  const [crop, setCrop] = useState<Crop>(initCrop)
  const [fadeUserGuide, setFadeUserGuide] = useState(false)
  const [saving, setSaving] = useState(false)

  const saveImage = async (newImage: HTMLImageElement): Promise<void> => {
    const croppedImage = await getCroppedImage(
      newImage,
      crop,
      isPng ? 'png' : 'jpeg',
    )
    const file = croppedImage && new File([croppedImage], imageToCrop.name)
    if (file) saveCrop(file)
    setCropImage(false)
    setSaving(false)
  }

  const handleImage = async (): Promise<void> => {
    setSaving(true)
    const newImage = new Image()
    newImage.src = URL.createObjectURL(imageToCrop.file)
    newImage.addEventListener('load', () => {
      saveImage(newImage)
    })
  }

  const renderUserGuide = (
    <UserGuide>
      <div className={`crop-help ${fadeUserGuide ? 'crop-help-fade-out' : ''}`}>
        <CgArrowsExpandDownRight className="crop-left-arrow" />
        <div>resize with handles</div>
        <CgArrowsExpandDownLeft className="crop-right-arrow" />
      </div>
    </UserGuide>
  )

  return (
    <StyledModal
      modalOpen={Boolean(imageToCrop)}
      onModalClose={(): void => setCropImage(false)}
      theme={{ fadeIn }}
    >
      <Styles>
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop): void => setCrop(percentCrop)}
          aspect={
            cropPixelAspect ? cropPixelAspect.x / cropPixelAspect.y : undefined
          }
          minWidth={cropPixelMinimumAspect && cropPixelMinimumAspect.x}
          minHeight={cropPixelMinimumAspect && cropPixelMinimumAspect.y}
          renderSelectionAddon={(state: ReactCropState): ReactElement => {
            if (state.cropIsActive) {
              setFadeUserGuide(true)
            }
            return renderUserGuide
          }}
          disabled={saving}
          keepSelection={true}
        >
          <img alt="Crop box" src={imageUrl} />
        </ReactCrop>
        <InputGroup>
          <Button
            buttonStyle={ButtonStyle.Secondary}
            onClick={(): void => setCropImage(false)}
          >
            Cancel
          </Button>
          <Button
            buttonStyle={ButtonStyle.Action}
            onClick={handleImage}
            loading={saving}
          >
            Save
          </Button>
        </InputGroup>
      </Styles>
    </StyledModal>
  )
}
export default ImageCrop
